import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import CircleIcon from "@mui/icons-material/Circle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { BoldText, Subtitle, Subtitle2 } from "../../styledComponents";
import {
    EmployeeData,
    VehicleData,
} from "../../../../models/monitorView/monitorViewTypes";
import {
    getShiftAndSlotDuration,
    getVehicleStatusText,
} from "../../monitorViewUtils";
import { employeeGroupListItemDto } from "../../../../models/employeeGroup/employeeGroup";
import { getWorkTimeTypeColorClass } from "../../../../models/workShitTimeSlot/workTimeType";
import { FlespiVehicleCameraData } from "../../../../models/vehicle/flespiDataTypes";
import { WorklogPopover } from "../popovers/workLogPopover";
import { DriveTimeLogPopover } from "../popovers/driveTimeLogPopover";
import { useTranslation } from "react-i18next";
import CameraStreamDialog from "./cameraStreamDialog";
import VehicleOrdersSection from "./vehicleOrdersSection";

interface FleetOverviewCardContentProps {
    vehicle: VehicleData;
    employee: EmployeeData;
    employeeGroups: employeeGroupListItemDto[];
    cameraData?: FlespiVehicleCameraData;
    onSelectEmployee: () => void;
}

const FleetOverviewCardContent: React.FC<FleetOverviewCardContentProps> = React.memo(({
    vehicle,
    employee,
    employeeGroups,
    cameraData,
    onSelectEmployee,
}) => {
    const workShiftTimeSlotAndDuration = useMemo(() => {
        if (employee) {
            return getShiftAndSlotDuration(
                employee.currentWorkShift ? [employee.currentWorkShift] : [],
                employee.employeeId
            );
        } else {
            return null;
        }
    }, [employee?.currentWorkShift, employee?.employeeId]);

    const salaryPeriodTypeId = employeeGroups?.find(
        (g) => employee && g.id === employee?.salaryGroupId
    )?.salaryPeriodTypeId;

    const isWorking = !!employee?.workTimeTypeName;

    return (
        <Grid container>
            <Grid item sm={12}>
                <TitleSection
                    vehicle={vehicle}
                    employee={employee}
                    workShiftTimeSlotEffectiveDate={
                        workShiftTimeSlotAndDuration?.effectiveDate
                    }
                    salaryPeriodTypeId={salaryPeriodTypeId}
                    cameraData={cameraData}
                    onSelectEmployee={onSelectEmployee}
                />
            </Grid>
            {vehicle && (
                <Grid item sm={12}>
                    <VehicleStatusSection
                        vehicle={vehicle}
                        employee={employee}
                    />
                </Grid>
            )}
            {employee && isWorking && (
                <Grid item sm={12}>
                    <WorktimeSection
                        timeSlotDuration={
                            workShiftTimeSlotAndDuration?.timeSlotDuration
                        }
                        employee={employee}
                        vehicle={vehicle}
                    />
                </Grid>
            )}
            {!isWorking && vehicle && (
                <Grid item sm={12}>
                    <NotWorkingSection vehicle={vehicle} />
                </Grid>
            )}
            {vehicle?.orders?.length > 0 && (
                <Grid item sm={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <VehicleOrdersSection orders={vehicle.orders} />
                </Grid>
            )}
        </Grid>
    );
});

FleetOverviewCardContent.displayName = "FleetOverviewCardContent";

interface TitleSectionProps {
    vehicle?: VehicleData;
    employee?: EmployeeData;
    workShiftTimeSlotEffectiveDate?: string;
    salaryPeriodTypeId?: string;
    cameraData?: FlespiVehicleCameraData;
    onSelectEmployee: () => void;
}

const TitleSection = ({
    vehicle,
    employee,
    workShiftTimeSlotEffectiveDate,
    salaryPeriodTypeId,
    cameraData,
    onSelectEmployee,
}: TitleSectionProps) => {
    const { t } = useTranslation();
    const [cameraDialogOpen, setCameraDialogOpen] = React.useState(false);

    const hasCamera = cameraData?.cameraDevices?.length > 0;

    return (
        <Grid container>
            <Grid item sm={9}>
                {vehicle ? (
                    <BoldText variant="h6"> {vehicle.registerNumber} </BoldText>
                ) : (
                    <BoldText>
                        <i>{t("monitorView.noVehicle")}</i>
                    </BoldText>
                )}
            </Grid>

            {/* Right top corner buttons */}
            <Grid item sm={3}>
                <Box sx={{ textAlign: "right " }}>
                    {vehicle && (
                        <Tooltip
                            title={
                                hasCamera
                                    ? t("monitorView.camera.hasCamera")
                                    : t("monitorView.camera.noCamera")
                            }
                        >
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (hasCamera) {
                                        setCameraDialogOpen(true);
                                    }
                                }}
                                style={
                                    hasCamera
                                        ? { cursor: "pointer" }
                                        : undefined
                                }
                            >
                                {hasCamera ? (
                                    <VideocamIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                ) : (
                                    <VideocamOffIcon
                                        fontSize="small"
                                        color="disabled"
                                    />
                                )}
                            </span>
                        </Tooltip>
                    )}
                    {hasCamera && cameraData && (
                        <CameraStreamDialog
                            open={cameraDialogOpen}
                            onClose={() => setCameraDialogOpen(false)}
                            vehicleRegisterNumber={
                                vehicle?.registerNumber ?? ""
                            }
                            cameraData={cameraData}
                        />
                    )}
                    {employee && (
                        <WorklogPopover
                            employeeId={employee.employeeId}
                            salaryPeriodTypeId={salaryPeriodTypeId}
                        />
                    )}
                    {employee && workShiftTimeSlotEffectiveDate && (
                        <a
                            href={`/worktimepage/details/${employee.employeeId}/${workShiftTimeSlotEffectiveDate}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <OpenInNewIcon fontSize="small" color="primary" />
                        </a>
                    )}
                </Box>
            </Grid>
            <Grid item sm={"auto"}>
                {vehicle?.brand ? (
                    <Subtitle variant="subtitle1"> {vehicle.brand} </Subtitle>
                ) : (
                    <BoldText>-</BoldText>
                )}
            </Grid>
            <Grid item xs={12}>
                {employee ? (
                    employee.workTimeTypeName ? (
                        <Subtitle
                            sx={{
                                cursor: "pointer",
                                color: "primary.main",
                                fontWeight: 600,
                                "&:hover": {
                                    color: "primary.dark",
                                },
                            }}
                            variant="subtitle1"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectEmployee();
                            }}
                        >
                            {employee.employeeName}
                        </Subtitle>
                    ) : (
                        <Subtitle variant="subtitle1">
                            {" "}
                            {employee.employeeName}
                        </Subtitle>
                    )
                ) : (
                    <></>
                )}
            </Grid>
        </Grid>
    );
};

interface VehicleStatusSectionProps {
    vehicle?: VehicleData;
    employee?: EmployeeData;
}

const VehicleStatusSection = ({
    vehicle,
    employee,
}: VehicleStatusSectionProps) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid container alignItems="center" sx={{ mb: 0.5 }}>
                <Grid item sm={5}>
                    <Typography variant="caption" color="text.secondary">
                        {t(
                            "monitorView.sidebar.overview.overviewCard.gpsStatus"
                        )}
                    </Typography>
                </Grid>

                <Grid item sm={3}>
                    <Typography variant="caption" color="text.secondary">
                        {t("monitorView.sidebar.overview.overviewCard.speed")}
                    </Typography>
                </Grid>

                <Grid
                    item
                    sm={4}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                    {/* empty header cell or icon */}
                </Grid>
            </Grid>
            {/* Content */}
            <Grid container alignItems="center">
                {/* GPS STATUS */}
                <Grid item sm={5}>
                    {vehicle ? (
                        <BoldText>
                            {getVehicleStatusText(vehicle.driving, t, false)}
                        </BoldText>
                    ) : (
                        <BoldText>-</BoldText>
                    )}
                </Grid>

                {/* SPEED */}
                <Grid item sm={4}>
                    <BoldText>
                        {vehicle?.speed ? `${vehicle.speed} km/h` : "-"}
                    </BoldText>
                </Grid>

                {/* ACTION */}
                <Grid
                    item
                    sm={3}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                    {employee && (
                        <DriveTimeLogPopover employeeId={employee.employeeId} />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

interface WorktimeSectionProps {
    employee: EmployeeData;
    timeSlotDuration: string;
    vehicle?: VehicleData;
}

const WorktimeSection = ({
    employee,
    timeSlotDuration,
    vehicle,
}: WorktimeSectionProps) => {
    const { t } = useTranslation();
    return (
        <>
            <Divider sx={{ my: 1 }} />
            <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                    <CircleIcon
                        className={`text-${getWorkTimeTypeColorClass(employee.workTimeTypeType)}`}
                        sx={{ display: "inline-block" }}
                    />
                    <BoldText
                        className={`text-${getWorkTimeTypeColorClass(employee.workTimeTypeType)}`}
                        sx={{ marginLeft: 1 }}
                    >
                        {employee.workTimeTypeName}
                    </BoldText>
                </Grid>
                <Grid item>
                    <BoldText
                        className={`text-${getWorkTimeTypeColorClass(
                            employee.workTimeTypeType
                        )}`}
                        sx={{ fontWeight: "bold" }}
                    >
                        {timeSlotDuration}
                    </BoldText>
                </Grid>
            </Grid>
            {/* Telematics as secondary info when working */}
            {vehicle && vehicle.driving >= 0 && (
                <Subtitle2>
                    {getVehicleStatusText(vehicle.driving, t, false)}
                    {vehicle.speed > 0 ? ` ${vehicle.speed} km/h` : ""}
                </Subtitle2>
            )}
        </>
    );
};

interface NotWorkingSectionProps {
    vehicle: VehicleData;
}

const NotWorkingSection = ({ vehicle }: NotWorkingSectionProps) => {
    const { t } = useTranslation();

    return (
        <>
            <Divider sx={{ my: 1 }} />
            <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                    <CircleIcon
                        className="text-gray-light"
                        sx={{ display: "inline-block" }}
                    />
                    <BoldText
                        className="text-gray-light"
                        sx={{ marginLeft: 1 }}
                    >
                        {t("monitorView.primaryFilter.notWorking")}
                    </BoldText>
                </Grid>
                <Grid item>
                    <Subtitle2>
                        {getVehicleStatusText(vehicle.driving, t, true)}
                    </Subtitle2>
                </Grid>
            </Grid>
        </>
    );
};

export default FleetOverviewCardContent;
